import { asInstanceOf, isAbsent, isDefined, isInstanceOf, Notifier, Option, panic, RuntimeNotifier, safeExecute, Terminator, UUID } from "@opendaw/lib-std";
import { BoxEditing, BoxGraph, DeleteUpdate, NewUpdate } from "@opendaw/lib-box";
import { ApparatDeviceBox, AudioFileBox, BoxIO, NoteEventBox, NoteEventCollectionBox, NoteRegionBox, SpielwerkDeviceBox, TrackBox, WerkstattDeviceBox } from "@opendaw/studio-boxes";
import { AudioUnitBoxAdapter, BoxAdapters, ColorCodes, DeviceBoxUtils, Devices, isModulatorBox, isModulatorBoxAdapter, isVertexOfBox, ParameterFieldAdapters, ProjectSkeleton, RegionAdapters, RemoteSelections, RootBoxAdapter, ScriptCompiler, TimelineBoxAdapter, TrackBoxAdapter, TrackType, UnionBoxTypes, UserEditingManager, Validator, VaryingTempoMap, VertexSelection } from "@opendaw/studio-adapters";
import { LiveStreamReceiver } from "@opendaw/lib-fusion";
import { BoxGraphCopy } from "../BoxGraphCopy";
import { Mixer } from "../Mixer";
import { ProjectApi } from "./ProjectApi";
import { NestedHostExit } from "./NestedHostExit";
import { ProjectMigration } from "./ProjectMigration";
import { CaptureDevices, CaptureMidi, Recording } from "../capture";
import { EngineFacade } from "../EngineFacade";
import { MidiDevices, MIDILearning } from "../midi";
import { ProjectValidation } from "./ProjectValidation";
import { StudioPreferences } from "../StudioPreferences";
import { RegionOverlapResolver, TimelineFocus } from "../ui";
import { SampleStorage } from "../samples";
import { AudioUnitFreeze } from "../AudioUnitFreeze";
import { AutomationSuspension } from "./AutomationSuspension";
// Main Entry Point for a Project
export class Project {
    static new(env, options) {
        const createDefaultUser = options?.noDefaultUser !== true;
        const createOutputMaximizer = StudioPreferences.settings.engine["auto-create-output-maximizer"];
        const { boxGraph, mandatoryBoxes } = ProjectSkeleton.empty({
            createOutputMaximizer,
            createDefaultUser
        });
        const project = new Project(env, boxGraph, mandatoryBoxes);
        if (createDefaultUser) {
            project.follow(mandatoryBoxes.userInterfaceBoxes[0]);
        }
        return project;
    }
    static load(env, arrayBuffer) {
        return this.fromSkeleton(env, ProjectSkeleton.decode(arrayBuffer));
    }
    static async loadAnyVersion(env, arrayBuffer) {
        console.debug("loadAnyVersion");
        const skeleton = ProjectSkeleton.decode(arrayBuffer);
        await ProjectMigration.migrate(env, skeleton);
        return this.fromSkeleton(env, skeleton);
    }
    static fromSkeleton(env, skeleton, followFirstUser = true) {
        ProjectValidation.validate(skeleton);
        const project = new Project(env, skeleton.boxGraph, skeleton.mandatoryBoxes);
        if (followFirstUser) {
            project.follow(project.userInterfaceBoxes[0]);
        }
        return project;
    }
    #midiOutNotifier = new Notifier();
    #terminator = new Terminator();
    #sampleRegistrations;
    #userCreatedSamples = UUID.newSet(uuid => uuid);
    #env;
    boxGraph;
    rootBox;
    userInterfaceBoxes;
    primaryAudioBusBox;
    primaryAudioUnitBox;
    timelineBox;
    api;
    captureDevices;
    editing;
    selection;
    deviceSelection;
    modulatorSelection;
    regionSelection;
    remoteSelections;
    remoteDeviceSelection;
    remoteRegionSelection;
    boxAdapters;
    userEditingManager;
    parameterFieldAdapters;
    liveStreamReceiver;
    midiLearning;
    mixer;
    tempoMap;
    overlapResolver;
    timelineFocus;
    engine = new EngineFacade();
    audioUnitFreeze;
    #rootBoxAdapter;
    #timelineBoxAdapter;
    #loadedScriptUuids = UUID.newSet(uuid => uuid);
    #terminated = false;
    constructor(env, boxGraph, { rootBox, userInterfaceBoxes, primaryAudioBusBox, primaryAudioUnitBox, timelineBox }) {
        this.#env = env;
        this.boxGraph = boxGraph;
        this.rootBox = rootBox;
        this.userInterfaceBoxes = userInterfaceBoxes;
        this.primaryAudioBusBox = primaryAudioBusBox;
        this.primaryAudioUnitBox = primaryAudioUnitBox;
        this.timelineBox = timelineBox;
        this.api = new ProjectApi(this);
        this.editing = env.createEditing?.(this.boxGraph) ?? new BoxEditing(this.boxGraph);
        this.selection = new VertexSelection(this.editing, this.boxGraph);
        this.parameterFieldAdapters = new ParameterFieldAdapters();
        this.boxAdapters = this.#terminator.own(new BoxAdapters(this));
        this.liveStreamReceiver = this.#terminator.own(new LiveStreamReceiver());
        this.#timelineBoxAdapter = this.boxAdapters.adapterFor(this.timelineBox, TimelineBoxAdapter);
        this.tempoMap = this.#terminator.own(new VaryingTempoMap(this.#timelineBoxAdapter));
        this.deviceSelection = this.#terminator.own(this.selection.createFilteredSelection(isVertexOfBox(DeviceBoxUtils.isDeviceBox), {
            fx: (adapter) => adapter.box,
            fy: vertex => this.boxAdapters.adapterFor(vertex.box, Devices.isAny)
        }));
        this.modulatorSelection = this.#terminator.own(this.selection.createFilteredSelection(isVertexOfBox(isModulatorBox), {
            fx: (adapter) => adapter.box,
            fy: vertex => this.boxAdapters.adapterFor(vertex.box, isModulatorBoxAdapter)
        }));
        this.regionSelection = this.#terminator.own(this.selection.createFilteredSelection(isVertexOfBox(UnionBoxTypes.isRegionBox), {
            fx: (adapter) => adapter.box,
            fy: vertex => RegionAdapters.for(this.boxAdapters, vertex.box)
        }));
        this.remoteSelections = this.#terminator.own(new RemoteSelections(this.rootBox, this.selection.user));
        this.remoteDeviceSelection = this.#terminator.own(this.remoteSelections.createFilteredSelection(isVertexOfBox(DeviceBoxUtils.isDeviceBox), {
            fx: (adapter) => adapter.box,
            fy: vertex => this.boxAdapters.adapterFor(vertex.box, Devices.isAny)
        }));
        this.remoteRegionSelection = this.#terminator.own(this.remoteSelections.createFilteredSelection(isVertexOfBox(UnionBoxTypes.isRegionBox), {
            fx: (adapter) => adapter.box,
            fy: vertex => RegionAdapters.for(this.boxAdapters, vertex.box)
        }));
        this.userEditingManager = new UserEditingManager(this.editing);
        this.#terminator.own(new NestedHostExit(this.boxAdapters, this.userEditingManager.audioUnit));
        this.midiLearning = this.#terminator.own(new MIDILearning(this));
        this.captureDevices = this.#terminator.own(new CaptureDevices(this));
        this.#rootBoxAdapter = this.boxAdapters.adapterFor(this.rootBox, RootBoxAdapter);
        this.mixer = new Mixer(this.#rootBoxAdapter.audioUnits, this.boxAdapters);
        this.overlapResolver = new RegionOverlapResolver(this.editing, this.api, this.boxAdapters);
        this.timelineFocus = this.#terminator.own(new TimelineFocus());
        this.audioUnitFreeze = this.#terminator.own(new AudioUnitFreeze(this));
        this.#terminator.own(AutomationSuspension.start(this));
        console.debug(`Project was created on ${this.rootBoxAdapter.created.toString()}`);
        this.#sampleRegistrations = UUID.newSet(({ uuid }) => uuid);
        for (const box of this.boxGraph.boxes()) {
            if (box instanceof AudioFileBox) {
                this.#registerSample(box.address.uuid);
            }
        }
        this.#terminator.own(this.boxGraph.subscribeToAllUpdates({
            onUpdate: (update) => {
                if (update instanceof NewUpdate && update.name === AudioFileBox.ClassName) {
                    this.#registerSample(update.uuid);
                }
                else if (update instanceof DeleteUpdate && update.name === AudioFileBox.ClassName) {
                    this.#unregisterSample(update.uuid);
                    this.#deleteUserCreatedSample(update.uuid).finally();
                }
            }
        }));
    }
    loadScriptDevices() {
        const audioContext = this.#env.audioWorklets.context;
        const loadScript = (config, deviceBox) => {
            if (this.#loadedScriptUuids.opt(deviceBox.address.uuid).nonEmpty()) {
                return;
            }
            this.#loadedScriptUuids.add(deviceBox.address.uuid);
            ScriptCompiler.create(config).load(audioContext, deviceBox).catch(reason => console.warn(`Failed to load script device ${UUID.toString(deviceBox.address.uuid)}:`, reason));
        };
        for (const box of this.boxGraph.boxes()) {
            if (box instanceof ApparatDeviceBox) {
                loadScript({ headerTag: "apparat", registryName: "apparatProcessors", functionName: "apparat" }, box);
            }
            else if (box instanceof WerkstattDeviceBox) {
                loadScript({ headerTag: "werkstatt", registryName: "werkstattProcessors", functionName: "werkstatt" }, box);
            }
            else if (box instanceof SpielwerkDeviceBox) {
                loadScript({ headerTag: "spielwerk", registryName: "spielwerkProcessors", functionName: "spielwerk" }, box);
            }
        }
    }
    startAudioWorklet(restart, options) {
        console.debug(`start AudioWorklet`);
        this.loadScriptDevices();
        const lifecycle = this.#terminator.spawn();
        const worklet = lifecycle.own(this.#env.audioWorklets.createEngine({ project: this, options }));
        const handler = async (event) => {
            console.warn(event);
            // we will only accept the first error
            worklet.removeEventListener("error", handler);
            worklet.removeEventListener("processorerror", handler);
            lifecycle.terminate();
            await safeExecute(restart?.unload, event);
            if (this.#terminated) {
                return;
            }
            safeExecute(restart?.load, this.startAudioWorklet(restart));
        };
        worklet.addEventListener("error", handler);
        worklet.addEventListener("processorerror", handler);
        worklet.connect(worklet.context.destination, 0);
        this.engine.setWorklet(worklet);
        worklet.isReady().then(() => this.audioUnitFreeze.replay(worklet));
        return worklet;
    }
    handleCpuOverload() {
        if (!StudioPreferences.settings.engine["stop-playback-when-overloading"]) {
            return;
        }
        this.engine.sleep();
        RuntimeNotifier.notify({ message: "CPU overload. Playback stopped.", icon: "Info" });
    }
    startRecording(countIn = true) {
        this.engine.assertWorklet();
        if (Recording.isRecording) {
            return;
        }
        Recording.start(this, countIn).finally();
    }
    stopRecording() {
        this.engine.stopRecording();
    }
    isRecording() { return Recording.isRecording; }
    commitMidiCapture() {
        if (Recording.isRecording) {
            return;
        }
        const armed = this.captureDevices.filterArmed()
            .filter((capture) => isInstanceOf(capture, CaptureMidi));
        if (armed.length === 0) {
            return;
        }
        const focusedTrack = this.timelineFocus.track;
        const target = focusedTrack
            .flatMap(track => track.optAudioUnit)
            .map(unit => unit.address.uuid)
            .flatMap(uuid => Option.wrap(armed.find(capture => UUID.equals(capture.audioUnitBox.address.uuid, uuid))))
            .unwrapOrElse(armed[0]);
        const result = target.resolveCapture();
        if (result.isEmpty()) {
            return;
        }
        const { mode, origin, notes } = result.unwrap();
        const firstPosition = notes[0].position;
        const regionPosition = mode === "playing"
            ? origin + firstPosition
            : this.engine.position.getValue();
        const regionDuration = notes.reduce((max, note) => Math.max(max, (note.position - firstPosition) + note.duration), 0);
        const targetUuid = target.audioUnitBox.address.uuid;
        const focusedMatch = focusedTrack
            .flatMap(track => track.optAudioUnit)
            .mapOr(unit => UUID.equals(unit.address.uuid, targetUuid), false);
        if (focusedMatch) {
            const track = focusedTrack.unwrap();
            this.#commitCapturedNotes(track, regionPosition, regionDuration, firstPosition, notes);
        }
        else {
            const noteTrackBox = target.audioUnitBox.tracks.pointerHub.incoming()
                .map(({ box }) => asInstanceOf(box, TrackBox))
                .find(trackBox => trackBox.type.getValue() === TrackType.Notes);
            if (isAbsent(noteTrackBox)) {
                return;
            }
            const track = this.boxAdapters.adapterFor(noteTrackBox, TrackBoxAdapter);
            this.#commitCapturedNotes(track, regionPosition, regionDuration, firstPosition, notes);
        }
        target.resetCapture();
    }
    #commitCapturedNotes(track, regionPosition, regionDuration, firstPosition, notes) {
        this.editing.modify(() => {
            const solver = this.overlapResolver.fromRange(track, regionPosition, regionPosition + regionDuration);
            const collection = NoteEventCollectionBox.create(this.boxGraph, UUID.generate());
            const regionBox = NoteRegionBox.create(this.boxGraph, UUID.generate(), box => {
                box.regions.refer(track.box.regions);
                box.events.refer(collection.owners);
                box.position.setValue(regionPosition);
                box.duration.setValue(regionDuration);
                box.loopDuration.setValue(regionDuration);
                box.hue.setValue(ColorCodes.forTrackType(TrackType.Notes));
                box.label.setValue("Captured");
            });
            for (const note of notes) {
                NoteEventBox.create(this.boxGraph, UUID.generate(), box => {
                    box.position.setValue(note.position - firstPosition);
                    box.duration.setValue(note.duration);
                    box.pitch.setValue(note.pitch);
                    box.velocity.setValue(note.velocity);
                    box.events.refer(collection.events);
                });
            }
            solver();
            this.selection.select(regionBox);
        });
    }
    subscribeMidiCaptureAvailable(observer) {
        const terminator = new Terminator();
        const inner = terminator.own(new Terminator());
        const rebuild = () => {
            inner.terminate();
            const midiCaptures = this.captureDevices.allCaptures()
                .filter((capture) => isInstanceOf(capture, CaptureMidi));
            const update = () => observer(midiCaptures.some(capture => capture.armed.getValue() && capture.captureNoteOnCount.getValue() > 0));
            for (const capture of midiCaptures) {
                inner.ownAll(capture.armed.subscribe(update), capture.captureNoteOnCount.subscribe(update));
            }
            update();
        };
        terminator.own(this.captureDevices.subscribeChanges(rebuild));
        rebuild();
        return terminator;
    }
    follow(box) {
        this.userEditingManager.follow(box);
        this.midiLearning.followUser(box.midiControllers);
        this.selection.switch(box.selection);
    }
    own(terminable) { return this.#terminator.own(terminable); }
    ownAll(...terminables) { return this.#terminator.ownAll(...terminables); }
    spawn() { return this.#terminator.spawn(); }
    get env() { return this.#env; }
    get rootBoxAdapter() { return this.#rootBoxAdapter; }
    get timelineBoxAdapter() { return this.#timelineBoxAdapter; }
    get sampleManager() { return this.#env.sampleManager; }
    get soundfontManager() { return this.#env.soundfontManager; }
    get clipSequencing() { return panic("Only available in audio context"); }
    get isAudioContext() { return false; }
    get isMainThread() { return true; }
    get primaryAudioUnitBoxAdapter() {
        return this.boxAdapters.adapterFor(this.primaryAudioUnitBox, AudioUnitBoxAdapter);
    }
    get liveStreamBroadcaster() { return panic("Only available in audio context"); }
    get skeleton() {
        return {
            boxGraph: this.boxGraph,
            mandatoryBoxes: {
                rootBox: this.rootBox,
                timelineBox: this.timelineBox,
                primaryAudioBusBox: this.primaryAudioBusBox,
                primaryAudioUnitBox: this.primaryAudioUnitBox,
                userInterfaceBoxes: this.userInterfaceBoxes
            }
        };
    }
    /// What THIS project's engine sent out, before it reaches the (globally shared) device. An internal sink
    /// like the shadertoy port must listen here: a second engine (a video export renders its own copy of the
    /// project) writes to the same device id, and its values would otherwise land in this project's sink.
    subscribeMIDIOut(observer) {
        return this.#midiOutNotifier.subscribe(observer);
    }
    receivedMIDIFromEngine(midiDeviceId, data, relativeTimeInMs) {
        this.#midiOutNotifier.notify({ deviceId: midiDeviceId, data });
        const timestamp = performance.now() + relativeTimeInMs;
        MidiDevices.findOutputDeviceById(midiDeviceId).ifSome(midiOutputDevice => {
            try {
                midiOutputDevice?.send(data, timestamp);
            }
            catch (reason) {
                console.warn("Failed to send MIDI message", reason);
            }
        });
    }
    collectSampleUUIDs() {
        return this.boxGraph.boxes()
            .filter(box => box.accept({ visitAudioFileBox: (_box) => true }))
            .map(box => box.address.uuid);
    }
    restartRecording() {
        if (this.engine.isRecording.getValue()) {
            const countingIn = Recording.wasCountingIn();
            this.engine.stopRecording();
            this.editing.modify(() => this.captureDevices.filterArmed()
                .forEach(capture => {
                capture.recordedRegions().forEach(region => region.box.delete());
                capture.clearRecordedRegions();
            }), false);
            this.engine.stop(true);
            this.engine.setPosition(Recording.wasStartingAt());
            const subscription = this.engine.isRecording.catchupAndSubscribe(owner => {
                if (!owner.getValue()) {
                    queueMicrotask(() => subscription.terminate());
                    this.startRecording(countingIn);
                }
            });
        }
        else {
            this.startRecording();
        }
    }
    toArrayBuffer() { return ProjectSkeleton.encode(this.boxGraph); }
    copy(env) {
        return Project.load(this.#mergeEnv(env), this.toArrayBuffer());
    }
    copyWithNewIdentities(env) {
        const data = BoxGraphCopy.serializeBoxes(this.boxGraph.boxes());
        const boxGraph = new BoxGraph(Option.wrap(BoxIO.create));
        boxGraph.beginTransaction();
        BoxGraphCopy.deserializeBoxes(data, boxGraph, { mapPointer: (_pointer, address) => address });
        boxGraph.endTransaction();
        boxGraph.verifyPointers();
        return Project.fromSkeleton(this.#mergeEnv(env), { boxGraph, mandatoryBoxes: ProjectSkeleton.findMandatoryBoxes(boxGraph) });
    }
    invalid() {
        const now = performance.now();
        const result = Validator.hasOverlappingRegions(this.boxGraph);
        if (performance.now() - now > 5) {
            console.warn("Evaluation of invalid project takes more than 5ms");
        }
        return result;
    }
    lastRegionAction() {
        return this.rootBoxAdapter.audioUnits.adapters()
            .flatMap(audioUnitAdapter => audioUnitAdapter.tracks.values()
            .map(trackAdapter => trackAdapter.regions.collection.asArray().at(-1)))
            .filter(isDefined).reduce((position, region) => Math.max(position, region.complete), 0);
    }
    trackUserCreatedSample(uuid) {
        this.#userCreatedSamples.add(uuid);
    }
    terminate() {
        this.#terminated = true;
        this.#sampleRegistrations.forEach(({ terminable }) => terminable.terminate());
        this.#sampleRegistrations.clear();
        this.#terminator.terminate();
    }
    #mergeEnv(env) {
        return {
            audioContext: env?.audioContext ?? this.#env.audioContext,
            audioWorklets: env?.audioWorklets ?? this.#env.audioWorklets,
            sampleManager: env?.sampleManager ?? this.#env.sampleManager,
            soundfontManager: env?.soundfontManager ?? this.#env.soundfontManager,
            sampleService: env?.sampleService ?? this.#env.sampleService,
            soundfontService: env?.soundfontService ?? this.#env.soundfontService,
            createEditing: env?.createEditing ?? this.#env.createEditing
        };
    }
    #registerSample(uuid) {
        const terminable = this.sampleManager.register(uuid);
        this.#sampleRegistrations.add({ uuid, terminable });
    }
    #unregisterSample(uuid) {
        this.#sampleRegistrations.removeByKey(uuid).terminable.terminate();
    }
    async #deleteUserCreatedSample(uuid) {
        if (!this.#userCreatedSamples.hasKey(uuid)) {
            return;
        }
        this.#userCreatedSamples.removeByKey(uuid);
        const autoDelete = StudioPreferences.settings.storage["auto-delete-orphaned-samples"];
        if (!autoDelete && await RuntimeNotifier.approve({
            headline: "Keep Sample?",
            message: "The sample is no longer used. Do you want to keep it in storage? Deletion of samples cannot be undone!",
            approveText: "Keep",
            cancelText: "Delete"
        })) {
            return;
        }
        SampleStorage.get().deleteItem(uuid).catch((reason) => console.warn("Failed to delete sample from storage", reason));
    }
}
